// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file useTypeahead.ts
 * @input Uses React useCallback/useRef
 * @output Exports useTypeahead hook for first-character (type-to-focus) search
 * @position Core hook; adds APG typeahead to menus, listboxes, and other
 *   collections. Composes with useListFocus/useGridFocus (or any collection)
 *   via a caller-supplied getItemLabels + onMatch.
 *
 * SYNC: When modified, update:
 * - /packages/core/src/hooks/index.ts
 */
import { useCallback, useRef } from 'react';
/**
 * Whether a key event represents a single printable character (a type-to-focus
 * candidate) rather than a control/navigation key or a shortcut chord.
 */
function isPrintableCharacter(e) {
  return e.key.length === 1 &&
  // Alt alone is not excluded: Option+letter on macOS composes a printable
  // character (Option+a → "å"), and dropping those makes accented labels
  // untypeable. Real chords still carry ctrl or meta — including AltGr,
  // which sets ctrlKey on Windows and Linux.
  !e.ctrlKey && !e.metaKey &&
  // A lone space is used for activation in menus, not typeahead-from-empty.
  e.key !== ' ';
}

/**
 * First-character ("type-ahead") search for a collection.
 *
 * Buffers printable keystrokes (resetting after `resetMs` of inactivity),
 * matches items whose label starts with the buffer, and cycles from the
 * current index — so pressing "s" repeatedly walks through the "s" items
 * instead of sticking on the first. Space is reserved for activation and is
 * only treated as typeahead while a buffer is already active.
 *
 * Additive and collection-agnostic: it does not move focus itself; the caller
 * wires `onMatch` to its own focus/selection (e.g. `useListFocus.focusItem`).
 *
 * @example
 * ```
 * const {listRef, handleKeyDown, focusItem, getItems} = useListFocus(...);
 * const typeahead = useTypeahead({
 *   getItemLabels: () => getItems().map(el => el.textContent),
 *   onMatch: focusItem,
 *   getCurrentIndex: () => getItems().findIndex(el => el === document.activeElement),
 * });
 * const onKeyDown = (e) => { if (!typeahead.onKeyDown(e)) handleKeyDown(e); };
 * ```
 */
export function useTypeahead(options) {
  const {
    getItemLabels,
    onMatch,
    getCurrentIndex,
    resetMs = 750,
    isDisabled
  } = options;
  const bufferRef = useRef('');
  const timeoutRef = useRef(undefined);
  const reset = useCallback(() => {
    bufferRef.current = '';
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
  }, []);
  const scheduleReset = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      bufferRef.current = '';
      timeoutRef.current = undefined;
    }, resetMs);
  }, [resetMs]);
  const onKeyDown = useCallback(e => {
    // A bare Space with no active buffer is not typeahead (menus activate on
    // Space); once the user is mid-typing, Space extends the query. Chorded
    // with ctrl or meta it is neither — that is an OS or IME shortcut, and
    // consuming it would append a raw space that poisons the whole buffer.
    const isSpaceMidType = e.key === ' ' && !e.ctrlKey && !e.metaKey && bufferRef.current.length > 0;
    if (!isPrintableCharacter(e) && !isSpaceMidType) {
      return false;
    }
    const labels = getItemLabels();
    if (labels.length === 0) {
      return false;
    }

    // Pressing the SAME single character repeatedly cycles to the next match
    // rather than filtering deeper (APG behavior).
    const char = e.key.toLowerCase();
    const isRepeatSameChar = bufferRef.current.length > 0 && bufferRef.current.split('').every(c => c === char);
    const nextBuffer = isRepeatSameChar ? char : bufferRef.current + char;
    bufferRef.current = nextBuffer;
    scheduleReset();
    const current = getCurrentIndex?.() ?? -1;
    const count = labels.length;
    const hasCurrent = current >= 0;
    const start = hasCurrent ? current : 0;
    // A single-character search starts AFTER the current item, so pressing a
    // letter walks to the next item beginning with it instead of re-matching
    // the one already selected (native <select> and APG getIndexByLetter).
    // Once the buffer is longer it is refining a match, so the current item
    // stays in range. With nothing current there is nothing to advance past.
    const offset = hasCurrent && nextBuffer.length === 1 ? 1 : 0;
    for (let i = 0; i < count; i++) {
      const index = (start + offset + i + count) % count;
      if (isDisabled?.(index)) {
        continue;
      }
      const label = labels[index];
      if (label != null && label.trim().toLowerCase().startsWith(nextBuffer)) {
        onMatch(index);
        return true;
      }
    }
    return true;
  }, [getItemLabels, onMatch, getCurrentIndex, scheduleReset, isDisabled]);
  return {
    onKeyDown,
    reset
  };
}